import { ABTestConfig, ABTestEvent } from "../types/ABTest";
import { AB_TEST_CONFIGS } from "../testConfigs/abTestConfigs";

// Storage keys under the `ab_test_` prefix that are NOT variant assignments.
// `ab_test_device_id` holds the per-device identifier (see generateSessionId),
// so without this exclusion getActiveVariants() reports a fake "device_id"
// test whose "variant" is a unique UUID per visitor — that gets stamped onto
// every draft/quote and turns the A/B report into a one-arm-per-visitor
// computation that hangs the server.
const NON_ASSIGNMENT_KEYS = new Set(["device_id"]);

// Only stamp testIds we still DEFINE. A returning visitor carries a 365-day
// `ab_test_<id>` cookie for EVERY test they were ever bucketed into, including
// concluded ones removed from AB_TEST_CONFIGS. Without this gate
// getActiveVariants() re-stamps those dead testIds onto fresh quotes/drafts, so
// retired tests keep re-appearing in the A/B dashboard catalog. Gating to the
// known set makes deleting a test's config the single switch that stops it
// accruing new data. (One-time cleanup of pre-existing stamps:
// scripts/cleanup_stale_ab_tests.ts.)
const KNOWN_TEST_IDS = new Set(
  Object.values(AB_TEST_CONFIGS).map((c) => c.testId),
);

class ABTestingService {
  private events: ABTestEvent[] = [];
  private sessionId: string;
  private storageType: "localStorage" | "cookies" = "localStorage";
  private disabledTests: Set<string> = new Set();
  // Server-driven resolution overlay (testId → winning variant key). Populated
  // imperatively by ABTestResolutionProvider from the public abTestResolutions
  // query. A resolved test routes 100% to its winner (see assignVariant arm 2b).
  private overlays: Map<string, string> = new Map();

  constructor() {
    this.sessionId = this.generateSessionId();
    this.loadDisabledTests();
  }

  // Apply the server resolution overlay. Replaces the whole map so a reopened
  // (now-inactive) test drops out and reverts to live assignment.
  applyServerOverlays(
    list: Array<{ testId: string; resolvedVariant: string }>,
  ): void {
    this.overlays = new Map(list.map((o) => [o.testId, o.resolvedVariant]));
  }

  setStorageType(type: "localStorage" | "cookies") {
    this.storageType = type;
  }

  private loadDisabledTests(): void {
    const key = "ab_tests_disabled";
    if (this.storageType === "cookies") {
      const value = this.getCookie(key);
      if (value) {
        try {
          this.disabledTests = new Set(JSON.parse(decodeURIComponent(value)));
        } catch {
          this.disabledTests = new Set();
        }
      }
    } else {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          this.disabledTests = new Set(JSON.parse(value));
        } catch {
          this.disabledTests = new Set();
        }
      }
    }
  }

  private saveDisabledTests(): void {
    const key = "ab_tests_disabled";
    const value = JSON.stringify(Array.from(this.disabledTests));

    if (this.storageType === "cookies") {
      this.setCookie(key, encodeURIComponent(value), 365);
    } else {
      localStorage.setItem(key, value);
    }
  }

  disableTest(testId: string): void {
    this.disabledTests.add(testId);
    this.saveDisabledTests();
  }

  enableTest(testId: string): void {
    this.disabledTests.delete(testId);
    this.saveDisabledTests();
  }

  toggleTest(testId: string): void {
    if (this.disabledTests.has(testId)) {
      this.enableTest(testId);
    } else {
      this.disableTest(testId);
    }
  }

  isTestDisabled(testId: string): boolean {
    return this.disabledTests.has(testId);
  }

  private generateSessionId(): string {
    const key = "ab_test_device_id";
    // Check cookie first (persistent across reloads)
    let deviceId = this.getCookie(key);
    if (!deviceId) {
      // Generate new device ID and persist to cookie (365 days)
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.setCookie(key, deviceId, 365);
    }
    return deviceId || "";
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash) % 1000000;
  }

  private selectVariantByHash(
    hash: number,
    variants: Record<string, any>,
  ): string {
    const normalizedHash = (hash % 10000) / 10000;
    const variantKeys = Object.keys(variants);
    let cumulativeWeight = 0;

    for (const key of variantKeys) {
      // `?? 0.5` (not `|| 0.5`): an explicit weight of 0 must mean 0, not the
      // 0.5 default. With `||`, a 100/0 ramp (control: 0.0) silently treated
      // control as 0.5 — half of fresh devices still bucketed into the
      // zero-weight arm. Only a missing/undefined weight falls back to 0.5.
      const weight = variants[key].weight ?? 0.5;
      if (normalizedHash < cumulativeWeight + weight) {
        return key;
      }
      cumulativeWeight += weight;
    }

    return variantKeys[0] || "default";
  }

  assignVariant(testId: string, config: ABTestConfig, userId?: string): string {
    // If test is disabled, always return the control variant
    if (this.isTestDisabled(testId)) {
      return "control";
    }

    // If test is finished, always return the resolved variant (ignore cookies)
    if (config.status === "finished" && config.resolvedVariant) {
      return config.resolvedVariant;
    }

    // Arm 2b — server-driven resolution overlay (ended from the dashboard).
    // Wins over the 365-day sticky cookie below, mirroring the `finished`
    // short-circuit, so a returning visitor cookied into "control" still sees
    // the resolved winner. MUST NOT storeVariant (don't bake a server decision
    // into a year-long cookie) and MUST NOT emit an exposure (resolved tests
    // stop accruing exposures, which is why the snapshot is the durable record).
    const overlaid = this.overlays.get(testId);
    if (overlaid) {
      return overlaid;
    }

    // Sticky assignment — but only if the stored value is a REAL variant key
    // of this test. A hand-set or stale cookie (e.g. "treatment" instead of
    // "photo_gate") would otherwise become the returned arm verbatim: every
    // isVariant() check misses (the UI silently falls back to control-style
    // rendering) while getActiveVariants() stamps the junk string onto the
    // quote, creating a phantom arm in the report. Unknown values are treated
    // as absent and overwritten by a fresh deterministic assignment below.
    // hasOwnProperty (not bracket access): a cookie valued "constructor" /
    // "toString" / "__proto__" would pass a truthiness check via the
    // Object.prototype chain and sneak through as a phantom arm.
    const storedVariant = this.getStoredVariant(testId);
    if (
      storedVariant &&
      config.variants &&
      Object.prototype.hasOwnProperty.call(config.variants, storedVariant)
    ) {
      return storedVariant;
    }

    const identifier = userId || this.sessionId;
    const hash = this.hashString(`${testId}:${identifier}`);
    const variant = this.selectVariantByHash(hash, config.variants);

    this.storeVariant(testId, variant);
    this.trackEvent(testId, variant, "exposure");

    return variant;
  }

  private storeVariant(testId: string, variant: string): void {
    const key = `ab_test_${testId}`;

    if (this.storageType === "cookies") {
      this.setCookie(key, variant, 365);
    } else {
      localStorage.setItem(key, variant);
    }
  }

  private getStoredVariant(testId: string): string | null {
    const key = `ab_test_${testId}`;

    if (this.storageType === "cookies") {
      return this.getCookie(key);
    } else {
      return localStorage.getItem(key);
    }
  }

  private setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = date.toUTCString();
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; expires=${expires}; max-age=${maxAge}; path=/`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(nameEQ)) {
        return trimmed.substring(nameEQ.length);
      }
    }
    return null;
  }

  trackEvent(testId: string, variant: string, eventType: string): void {
    const event: ABTestEvent = {
      testId,
      variantKey: variant,
      eventType: eventType as "exposure" | "conversion" | "custom",
      timestamp: Date.now(),
    };
    this.events.push(event);

    // Keep only last 100 events
    if (this.events.length > 100) {
      this.events = this.events.slice(-100);
    }
  }

  trackConversion(testId: string, variant: string): void {
    this.trackEvent(testId, variant, "conversion");
  }

  getEvents(): ABTestEvent[] {
    return [...this.events];
  }

  clearVariant(testId: string): void {
    const key = `ab_test_${testId}`;
    if (this.storageType === "cookies") {
      this.setCookie(key, "", -1);
    } else {
      localStorage.removeItem(key);
    }
  }

  clearAllVariants(): void {
    this.events = [];
    if (this.storageType === "cookies") {
      const expireCookie = (name: string) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
      };
      for (let i = 0; i < document.cookie.split(";").length; i++) {
        const cookie = document.cookie.split(";")[i].trim().split("=")[0];
        if (cookie.startsWith("ab_test_")) {
          expireCookie(cookie);
        }
      }
    } else {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("ab_test_")) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  }

  /**
   * Gets all currently active A/B test variants
   * Returns an array of objects with testId, testName, and variant information
   */
  getActiveVariants(): Array<{
    testId: string;
    testName?: string;
    variant: string;
    weight?: number;
  }> {
    // This will be populated from AB_TEST_CONFIGS in the component
    // For now, we just return the variants stored in cookies/localStorage
    const activeVariants: Array<{
      testId: string;
      testName?: string;
      variant: string;
      weight?: number;
    }> = [];

    // Must never throw: callers sit on funnel-critical paths (draft/quote
    // stamping inside submit onCompleted). A malformed cookie value (e.g. a
    // stray "%" makes decodeURIComponent throw URIError) skips that entry
    // only; any broader storage failure returns what was collected so far.
    try {
      // Landing-page assignments (PHP-set, ta_ab_* cookies). Same KNOWN_TEST_IDS
      // gate as funnel cookies so retired/unknown tests never stamp.
      const seen = new Set<string>();
      for (const cookie of document.cookie.split(";")) {
        const trimmed = cookie.trim();
        if (!trimmed.startsWith("ta_ab_")) continue;
        const [keyPart, valuePart] = trimmed.split("=");
        const testId = keyPart.replace("ta_ab_", "");
        if (!KNOWN_TEST_IDS.has(testId) || seen.has(testId)) continue;
        try {
          activeVariants.push({ testId, variant: decodeURIComponent(valuePart || "") });
          seen.add(testId);
        } catch {
          // Malformed cookie value — skip this entry.
        }
      }

      if (this.storageType === "cookies") {
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
          const trimmed = cookie.trim();
          if (trimmed.startsWith("ab_test_")) {
            const [keyPart, valuePart] = trimmed.split("=");
            const testId = keyPart.replace("ab_test_", "");
            if (NON_ASSIGNMENT_KEYS.has(testId)) continue;
            if (!KNOWN_TEST_IDS.has(testId)) continue; // skip retired tests
            if (seen.has(testId)) continue;
            try {
              activeVariants.push({
                testId,
                variant: decodeURIComponent(valuePart || ""),
              });
            } catch {
              // Malformed cookie value — skip this entry.
            }
          }
        }
      } else {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("ab_test_")) {
            const testId = key.replace("ab_test_", "");
            if (NON_ASSIGNMENT_KEYS.has(testId)) continue;
            if (!KNOWN_TEST_IDS.has(testId)) continue; // skip retired tests
            const variant = localStorage.getItem(key);
            if (variant) {
              activeVariants.push({
                testId,
                variant,
              });
            }
          }
        }
      }
    } catch {
      // Storage unavailable (private mode, sandboxed iframe) — return what
      // we have rather than breaking the funnel.
    }

    return activeVariants;
  }
}

export const abTestingService = new ABTestingService();
